"use client";
import React from "react";
import Image from "next/image";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";
import Link from "next/link";

// Interfaces
interface ContentTextProps {
    title: string;
    text: string;
}

interface Button {
    id: string;
    url: string;
    text: string;
    type: string;
    newTab: boolean;
}

interface Picture {
    data: {
        id: string;
        attributes: {
            url: string;
            name: string;
            alternativeText: string;
        };
    };
}

interface ContentProps {
    data: {
        id: number;
        title: string;
        subtitle: string;
        picture: Picture;
        isReversed: boolean;
        contentTexts: ContentTextProps[];
        button: Button[];
        isLive: boolean;
        useH1Tag?: boolean;
        variant?: "home" | "patent";
        bgColor?: string;
    };
}


// Main Component
export default function Content_Home_Layout1({ data }: ContentProps) {
    const imgUrl = getStrapiMedia(data.picture.data.attributes.url);
    if (!data.isLive) return null;

    return (
        <>
            <section className="relative w-full overflow-hidden min-h-screen lg:min-h-fit bg-gradient-to-br from-[var(--primary)] to-[var(--secondary-light)]">

                {/* Decorative texture and top accent */}
                <div className="absolute inset-0 opacity-5 bg-[url('/assets/luxury-texture.png')] bg-cover"></div>
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[var(--secondary)] to-transparent"></div>

                <div className="container mx-auto h-full flex flex-col-reverse lg:flex-row items-center px-4 sm:px-6 md:px-8 py-10 sm:py-10 gap-8">
                    {/* Text Content */}
                    <div className="lg:w-1/2 z-10">
                        <div className="mb-6 sm:mb-8 flex items-center gap-2 sm:gap-3">
                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--text-1)]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                            </svg>
                            <span className="text-[10px] sm:text-xs uppercase tracking-wider text-[var(--text-1)] font-medium">
                                Certified Organic
                            </span>
                        </div>

                        <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-normal text-[var(--accent-2)] leading-tight mb-4 sm:mb-6">
                            {data.title?.split(",").map((part: string, index: number) =>
                                index === 0 ? (
                                    <span
                                        key={index}
                                        className="block   italic text-[var(--text-1)]"
                                    >
                                        {part}
                                    </span>
                                ) : (
                                    part
                                )
                            )}
                        </h1>


                        <p className="text-sm sm:text-base md:text-lg text-[var(--accent-1)]/90 max-w-md sm:max-w-lg mb-6 sm:mb-8 leading-relaxed">
                            {data.subtitle}
                        </p>

                        <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-10">
                            {data.contentTexts?.map((section, index) => (
                                <li key={index} className="flex items-center">
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[var(--secondary-light)] flex items-center justify-center mr-2 sm:mr-3">
                                        <svg className="w-3 h-3 text-[var(--text-1)]" viewBox="0 0 20 20" fill="currentColor">
                                            <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <span className="text-sm sm:text-base text-[var(--accent-2)] font-medium">{section.title}</span>
                                </li>
                            ))}
                        </ul>

                        {data.button && data.button[0] && (
                            <Link
                                href={data.button[0].url || "/collection"}
                                target={data.button[0].newTab ? "_blank" : "_self"}
                                className="group relative inline-block px-6 sm:px-8 py-3 sm:py-4 overflow-hidden text-base sm:text-lg font-medium tracking-wider text-white rounded-full"
                            >
                                <span className="relative z-10 flex items-center">
                                    {data.button[0].text}
                                    <svg
                                        className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                                        />
                                    </svg>
                                </span>
                                <span className="absolute inset-0 bg-[var(--bg-col-2)]"></span>
                                <span className="absolute inset-0 bg-gradient-to-r from-[var(--secondary)] to-[var(--bg-col-2)] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                            </Link>
                        )}



                    </div>

                    {/* Image Content */}
                    <div className="lg:w-1/2 w-full h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] relative">
                        <div className="absolute inset-0 bg-[var(--secondary-light)] rounded-[24px] overflow-hidden flex items-center justify-center">
                            <Image
                                src={imgUrl || "/placeholder.jpg"}
                                alt={data.picture?.data?.attributes?.alternativeText || "Premium herbal product"}
                                fill
                                className="object-cover w-full h-full object-left"
                                sizes="(max-width: 768px) 100vw, 50vw"
                                priority
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.onerror = null;
                                    target.style.display = 'none';
                                    const fallback = document.getElementById('image-fallback');
                                    if (fallback) fallback.style.display = 'block';
                                }}
                            />
                            <div id="image-fallback" className="hidden w-full h-full">
                                <svg className="w-full h-full text-[var(--text-1)]" viewBox="0 0 800 1000" fill="currentColor">
                                    <rect width="800" height="1000" fill="#F5E9D9" />
                                    <text
                                        x="50%"
                                        y="50%"
                                        fontSize="24"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fill="#A67C52"
                                        fontFamily="serif"
                                    >
                                        QuickHenna Premium
                                    </text>
                                </svg>
                            </div>
                        </div>

                        <div className="absolute inset-0 border-[10px] sm:border-[12px] border-white/10 rounded-[24px] rotate-1 z-10 pointer-events-none"></div>
                        <div className="absolute -bottom-10 -left-10 w-40 h-40 sm:w-48 sm:h-48 bg-[var(--secondary-light)] rounded-full blur-3xl opacity-30"></div>
                    </div>
                </div>

                {/* Bottom Accent */}
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[var(--secondary)] to-transparent"></div>
            </section>
        </>
    );
}
